import config from "../config";
import { trackServerUnavailable } from "../lib/analytics";

/**
 * Circuit breaker for consecutive Composer/API failures.
 *
 * After `config.maxFailCount` failures inside `config.resetTime`, the user is
 * sent to `/server-down`. That page must stay in GA page_view so outages remain
 * visible. It must never hard-reload itself: a health-poll failure used to
 * re-enter this redirect and mint tens of thousands of page_views from one tab.
 */

export const SERVER_DOWN_PATH = "/server-down";

export const SERVER_DOWN_CAUSES = Object.freeze({
  API_FAILURES: "api_failures",
  AUTH_500: "auth_500",
  DIRECT: "direct",
});

const NAVIGATION_CAUSES = new Set([
  SERVER_DOWN_CAUSES.API_FAILURES,
  SERVER_DOWN_CAUSES.AUTH_500,
]);

const KNOWN_CAUSES = new Set([
  SERVER_DOWN_CAUSES.API_FAILURES,
  SERVER_DOWN_CAUSES.AUTH_500,
  SERVER_DOWN_CAUSES.DIRECT,
]);

const REDIRECT_FROM_KEY = "serverDownRedirectFrom";
const CAUSE_KEY = "serverDownCause";
const VISIT_CONTEXT_KEY = "nrs_server_down_visit";
const EVENT_RECORDED_KEY = "nrs_server_unavailable_event";

/**
 * True for `/server-down` and nested paths, including a stored pathname+search.
 *
 * @param {unknown} path
 * @returns {boolean}
 */
export function isServerDownPath(path) {
  if (typeof path !== "string" || path.length === 0) {
    return false;
  }
  const pathname = path.split("?")[0];
  return (
    pathname === SERVER_DOWN_PATH || pathname.startsWith(`${SERVER_DOWN_PATH}/`)
  );
}

/**
 * Records why and from where we are sending the user to `/server-down`.
 * No-ops when already on that page so a poll cannot clobber the original path
 * or trigger another navigation.
 *
 * @param {string} cause
 * @returns {boolean} true when the caller should navigate
 */
export function beginServerDownNavigation(cause) {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  if (!NAVIGATION_CAUSES.has(cause)) {
    throw new Error(`Invalid server-down navigation cause: ${String(cause)}`);
  }
  if (isServerDownPath(window.location.pathname)) {
    return false;
  }
  localStorage.setItem(
    REDIRECT_FROM_KEY,
    window.location.pathname + window.location.search
  );
  localStorage.setItem(CAUSE_KEY, cause);
  return true;
}

/**
 * Hard-navigates to `/server-down` unless the tab is already there.
 *
 * @param {string} cause
 */
export function redirectToServerDown(cause) {
  if (!beginServerDownNavigation(cause)) {
    return;
  }
  window.location.href = SERVER_DOWN_PATH;
}

/**
 * Auth-form entry: SPA navigation after stamping cause + return path.
 *
 * @param {(path: string) => void} push
 */
export function navigateAuthToServerDown(push) {
  if (!beginServerDownNavigation(SERVER_DOWN_CAUSES.AUTH_500)) {
    return;
  }
  push(SERVER_DOWN_PATH);
}

/**
 * Idempotent per tab: first call reads the redirect cause, later calls reuse it.
 * Missing or unknown stored values classify as `direct` (typed URL / bookmark).
 *
 * @returns {{ cause: string }}
 */
export function takeServerDownVisitContext() {
  if (typeof window === "undefined" || !window.sessionStorage || !window.localStorage) {
    throw new Error("takeServerDownVisitContext requires browser storage");
  }
  const existing = window.sessionStorage.getItem(VISIT_CONTEXT_KEY);
  const parsed = parseVisitContext(existing);
  if (parsed) {
    return parsed;
  }
  const stored = window.localStorage.getItem(CAUSE_KEY);
  localStorage.removeItem(CAUSE_KEY);
  const cause = KNOWN_CAUSES.has(stored) ? stored : SERVER_DOWN_CAUSES.DIRECT;
  const ctx = { cause };
  window.sessionStorage.setItem(VISIT_CONTEXT_KEY, JSON.stringify(ctx));
  return ctx;
}

/**
 * Return path after recovery. Never returns `/server-down`.
 *
 * @returns {string}
 */
export function resolveServerDownRedirectTo() {
  if (typeof window === "undefined" || !window.localStorage) {
    throw new Error("resolveServerDownRedirectTo requires browser localStorage");
  }
  const stored = localStorage.getItem(REDIRECT_FROM_KEY);
  if (!stored || isServerDownPath(stored)) {
    return "/";
  }
  return stored;
}

/**
 * Clears circuit-breaker visit keys after a successful recovery redirect.
 */
export function clearServerDownVisit() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage?.removeItem(REDIRECT_FROM_KEY);
  window.localStorage?.removeItem(CAUSE_KEY);
  window.sessionStorage?.removeItem(VISIT_CONTEXT_KEY);
  window.sessionStorage?.removeItem(EVENT_RECORDED_KEY);
}

/**
 * @returns {boolean}
 */
export function hasRecordedServerUnavailableEvent() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return false;
  }
  return sessionStorage.getItem(EVENT_RECORDED_KEY) === "1";
}

/**
 * Marks the one-shot `server_unavailable` event as sent for this tab visit.
 */
export function markServerUnavailableEventRecorded() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  sessionStorage.setItem(EVENT_RECORDED_KEY, "1");
}

/**
 * Emits `server_unavailable` once per tab visit after the first health check.
 *
 * @param {boolean} healthStillDown
 */
export function emitServerUnavailableOnce(healthStillDown) {
  if (hasRecordedServerUnavailableEvent()) {
    return;
  }
  markServerUnavailableEventRecorded();
  const { cause } = takeServerDownVisitContext();
  trackServerUnavailable({
    cause,
    healthStillDown,
  });
}

/**
 * @param {unknown} raw
 * @returns {{ cause: string } | null}
 */
function parseVisitContext(raw) {
  if (typeof raw !== "string" || raw.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && KNOWN_CAUSES.has(parsed.cause)) {
      return { cause: parsed.cause };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Resets the consecutive-failure counter.
 */
export function resetFailureCount() {
  if (typeof window !== "undefined" && window.localStorage) {
    localStorage.setItem("apiFailureCount", "0");
    localStorage.setItem("lastApiCallTime", new Date().getTime().toString());
  }
}

/**
 * Counts consecutive API failures and redirects once the threshold is reached.
 * Does not navigate when already on `/server-down`.
 */
export function incrementFailureCount() {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    const lastCallTime = localStorage.getItem("lastApiCallTime");
    const currentTime = new Date().getTime();

    if (lastCallTime && currentTime - lastCallTime > config.resetTime) {
      resetFailureCount();
    }

    let failureCount = parseInt(localStorage.getItem("apiFailureCount") || "0", 10);
    if (Number.isNaN(failureCount)) {
      failureCount = 0;
    }
    failureCount++;

    localStorage.setItem("apiFailureCount", failureCount.toString());
    localStorage.setItem("lastApiCallTime", currentTime.toString());

    if (failureCount >= config.maxFailCount) {
      redirectToServerDown(SERVER_DOWN_CAUSES.API_FAILURES);
    }
  } catch (err) {
    console.error("Error incrementing failure count:", err);
  }
}
